import type { Route } from './+types/services.residential';
import { Link } from 'react-router';
import { buildMeta, faqPageSchema, schemaFor, breadcrumbSchema } from '~/lib/seo';
import { Breadcrumbs, breadcrumbSchemaItems } from '~/components/breadcrumbs';
import { SITE } from '~/data/site';
import { PricingCalculatorWidget } from '~/components/marketing/PricingCalculatorWidget';
import { scopeFor, scopeItemCount, SERVICE_SCOPE, ADDONS } from '~/data/scope';
import { faqsFor } from '~/data/page-schemas';
import { formatAddon } from '@shared/pricing';
import '~/styles/marketing/services-residential.css';

export const meta: Route.MetaFunction = () => {
  return [
    ...buildMeta({
      title: 'Residential House Cleaning St. Petersburg FL',
      description:
        'Weekly, biweekly & monthly house cleaning in St. Petersburg, FL. Eco-friendly products, W2 employees, same cleaner every visit. Instant pricing by square footage.',
      path: '/residential',
      jsonLd: [
        schemaFor('/residential'),
        breadcrumbSchema(breadcrumbSchemaItems('/residential')),
      ].filter((s): s is Record<string, unknown> => !!s),
    }),
    {
      // Same list the page body renders — see faqsFor(). Was a third hardcoded
      // copy here, which is how visible text and structured data drift apart.
      'script:ld+json': faqPageSchema(faqsFor('/residential')),
    },
  ];
};

export default function ServicePage() {
  return (
    <>
      {/* HERO */}
      <section className="page-hero" id="main-content">
        <div className="page-hero-inner">
          <p className="breadcrumb">
            <Link to="/">Home</Link>
            <span className="sep">&rsaquo;</span>
            Residential
          </p>
          <h1>
            Reclaim Your Weekends.
            <br />
            We&rsquo;ll Handle the Cleaning.
          </h1>
          <p className="hero-sub">
            Residential home cleaning for Tampa Bay families &mdash; eco-friendly, W2 employees, and the same cleaner every visit.
          </p>
          <div className="page-hero-buttons">
            <Link to="/get-a-quote" className="btn btn-primary">
              Get a Free Quote &rarr;
            </Link>
          </div>
          <div className="page-hero-trust">
            <span>&#10003; Eco-Friendly Products</span>
            <span>&#10003; Same cleaner every visit</span>
            <span>&#10003; Photo report after every clean</span>
          </div>
        </div>
      </section>

      {/* ── PRICE + SCOPE, SIDE BY SIDE ──────────────────────
          Was two stacked full-width sections (calculator, then the checklist).
          Vikelas asked for the calculator on the RIGHT and "What's Included" on
          the LEFT, so the buyer reads what they get while pricing it.
          The checklist is composed from the scope truth doc — see
          src/app/data/scope.ts. Do not hardcode a list here again. */}
      <section className="sr-price-scope">
        <div className="container">
          <div className="sr-price-scope__head">
            <span className="eyebrow">Instant pricing</span>
            <h2 className="display-md">See your exact price in seconds.</h2>
            <p className="lede">
              Every visit runs the same {scopeItemCount('recurring')}-point checklist.
              Price it yourself on the right &mdash; what you see is what you pay.
            </p>
          </div>

          <div className="sr-price-scope__grid">
            {/* LEFT — what you actually get */}
            <div className="sr-scope">
              <h3 className="sr-scope__title">What&rsquo;s included in every clean</h3>
              <div className="sr-scope__groups">
                {scopeFor('recurring').map((group) => (
                  <div className="sr-scope__group" key={group.title}>
                    <h4>{group.title}</h4>
                    <ul>
                      {group.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              <div className="sr-scope__addons">
                <h4>Optional add-ons</h4>
                <ul>
                  {ADDONS.map((a) => (
                    <li key={a.key}>
                      <span>{a.label}</span>
                      <span className="sr-scope__addon-price">{formatAddon(a.key)}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="sr-scope__excludes">
                <h4>Not included</h4>
                <p>{SERVICE_SCOPE.recurring.excludes.join(' · ')}</p>
              </div>
            </div>

            {/* RIGHT — the calculator, sticky so it stays with the list */}
            <div className="sr-calc">
              <PricingCalculatorWidget />
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <h2>How It Works</h2>
            <p className="section-intro">
              We respond with an upfront price within 10 minutes. No estimates that change later.
            </p>
          </div>

          <div className="process-steps-row">
            <div className="process-step-item">
              <div className="step-num">01</div>
              <h3>Get Your Quote</h3>
              <p>
                Use the calculator above or send us a message. We&rsquo;ll respond within 10 minutes to confirm your booking.
              </p>
            </div>

            <div className="process-step-item">
              <div className="step-num">02</div>
              <h3>Your First Deep Clean</h3>
              <p>
                We get your home to baseline. Required for new recurring clients so we can maintain it efficiently going forward.
              </p>
            </div>

            <div className="process-step-item">
              <div className="step-num">03</div>
              <h3>Set It and Forget It</h3>
              <p>
                We show up on your schedule &mdash; every week, every two weeks, or every four weeks. You&rsquo;ll get timestamped photos after every clean.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY RECURRING CLIENTS LOVE US */}
      <section className="why-section">
        <div className="container">
          <div className="section-header">
            <h2>Why Recurring Clients Love Us</h2>
            <p className="section-intro">
              Recurring visits cost less per clean than one-time service. Set your rhythm and forget it.
            </p>
          </div>

          <div className="why-grid">
            <div className="why-card">
              <div className="card-icon"></div>
              <h3>Eco-Friendly Always</h3>
              <p>
                Non-toxic, eco-friendly products. Safe for kids, pets, and the planet &mdash; without sacrificing a deep clean.
              </p>
            </div>

            <div className="why-card">
              <div className="card-icon"></div>
              <h3>Known for Our Detail</h3>
              <p>
                We&rsquo;re known for the level of detail in every cleaning &mdash; nothing gets missed, nothing gets rushed.
              </p>
            </div>

            <div className="why-card">
              <div className="card-icon"></div>
              <h3>Photo Verification</h3>
              <p>
                Timestamped before-and-after photos delivered to you after every single visit. Accountability built in.
              </p>
            </div>

            <div className="why-card">
              <div className="card-icon"></div>
              <h3>100% Satisfaction Guarantee</h3>
              <p>
                Not happy? We come back, no charge, no questions asked. We stand behind every clean we do.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section faq-section-offwhite">
        <div className="container">
          <div className="section-header centered">
            <h2>Frequently Asked Questions</h2>
            <p className="section-intro">
              Use the calculator above to see your exact price. Any questions beyond that, we&rsquo;re a quick text away.
            </p>
          </div>

          {/* Rendered from page-schemas.ts — the SAME list that becomes this
              page's FAQPage JSON-LD. Previously the markup here was a separate
              hardcoded copy that could drift from the schema, which suppresses
              the rich result. Add questions in page-schemas.ts, not here. */}
          <div className="faq-list">
            {faqsFor('/residential').map((faq) => (
              <details className="faq-item" key={faq.question}>
                <summary className="faq-question">{faq.question}</summary>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="page-cta">
        <div className="page-cta-inner">
          <h2>Ready to Get Started?</h2>
          <p className="page-cta-tagline">
            Typical response: under 10 minutes
          </p>
          <Link to="/get-a-quote" className="btn btn-dark">
            Get My Free Quote &rarr;
          </Link>
        </div>
      </section>
    </>
  );
}
